import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlusCircle, GripVertical, Trash2, Pencil } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import DocumentFormField from "./DocumentFormField";
import DocumentFormFieldDialog from "./DocumentFormFieldDialog";
import { documentOutputFormatApi } from "@/services/api";
import {
  DocumentOutputSection,
  DocumentOutputSubsection,
  DocumentOutputField,
} from "@/models/DocumentOutputFormat";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface DocumentFormSectionProps {
  section: DocumentOutputSection;
  onSectionUpdated: () => Promise<void>;
  onDeleteSection: (sectionId: number) => Promise<void>;
  dragHandleProps?: any;
}

const DocumentFormSection = ({
  section,
  onSectionUpdated,
  onDeleteSection,
  dragHandleProps,
}: DocumentFormSectionProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addSubsectionDialogOpen, setAddSubsectionDialogOpen] = useState(false);
  const [editSubsectionDialogOpen, setEditSubsectionDialogOpen] =
    useState(false);
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [selectedSubsectionId, setSelectedSubsectionId] = useState<
    number | null
  >(null);
  const [editingSubsection, setEditingSubsection] =
    useState<DocumentOutputSubsection | null>(null);
  const [sectionTitle, setSectionTitle] = useState(section.title);
  const [subsectionTitle, setSubsectionTitle] = useState("");
  const [subsectionName, setSubsectionName] = useState("");
  const [isSubsectionNameManuallyEntered, setIsSubsectionNameManuallyEntered] =
    useState(false);

  const subsections = section?.subsections || [];
  const sortedSubsections = [...subsections].sort(
    (a, b) => a.subsection_order - b.subsection_order
  );

  // Auto-generate subsection name from title when title changes (only if not manually entered)
  useEffect(() => {
    if (subsectionTitle && !isSubsectionNameManuallyEntered) {
      const autoGeneratedName = subsectionTitle
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "_")
        .trim();
      setSubsectionName(autoGeneratedName);
    }
  }, [subsectionTitle, isSubsectionNameManuallyEntered]);

  // Helper function to get subsection name with auto-generation fallback
  const getSubsectionName = (subsection: DocumentOutputSubsection) => {
    if (subsection.name) {
      return subsection.name;
    }
    // Auto-generate name from title
    return subsection.title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .trim();
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, type } = result;

    if (!destination || destination.index === source.index) return;

    try {
      setIsSubmitting(true);

      if (type === "subsections") {
        // Handle subsection reordering
        const reorderedSubsections = [...sortedSubsections];
        const [movedSubsection] = reorderedSubsections.splice(source.index, 1);
        reorderedSubsections.splice(destination.index, 0, movedSubsection);

        // Update subsection orders using the API
        const subsectionIds = reorderedSubsections.map((s) => s.id);
        await documentOutputFormatApi.reorderSubsections(
          section.id,
          subsectionIds
        );

        toast({
          title: "Subsections reordered",
          description: "The subsections have been reordered successfully",
        });
      } else if (type.startsWith("fields-")) {
        // Handle field reordering within a subsection
        const subsectionId = parseInt(type.replace("fields-", ""));
        const subsection = sortedSubsections.find((s) => s.id === subsectionId);

        if (subsection && subsection.fields) {
          const sortedFields = [...subsection.fields].sort(
            (a, b) => a.field_order - b.field_order
          );
          const reorderedFields = [...sortedFields];
          const [movedField] = reorderedFields.splice(source.index, 1);
          reorderedFields.splice(destination.index, 0, movedField);

          // Update field orders using the API
          const fieldIds = reorderedFields.map((f) => f.id);
          await documentOutputFormatApi.reorderFields(subsectionId, fieldIds);

          toast({
            title: "Fields reordered",
            description: "The fields have been reordered successfully",
          });
        }
      }

      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error reordering",
        description: error.message || "An error occurred while reordering",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSection = async () => {
    try {
      setIsSubmitting(true);

      // Update section using the API
      await documentOutputFormatApi.updateSection(section.id, {
        title: sectionTitle,
      });

      toast({
        title: "Section updated",
        description: "The section has been updated successfully",
      });

      setEditDialogOpen(false);
      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error updating section",
        description:
          error.message || "An error occurred while updating the section",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddSubsection = async () => {
    try {
      setIsSubmitting(true);

      // Auto-generate name from title if not provided
      const finalName =
        subsectionName.trim() ||
        subsectionTitle
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, "")
          .replace(/\s+/g, "_")
          .trim();

      // Create subsection using the API
      await documentOutputFormatApi.createSubsection(section.id, {
        title: subsectionTitle,
        name: finalName || undefined,
        subsection_order: subsections.length,
      });

      toast({
        title: "Subsection added",
        description: "The subsection has been added successfully",
      });

      setSubsectionTitle("");
      setSubsectionName("");
      setIsSubsectionNameManuallyEntered(false);
      setAddSubsectionDialogOpen(false);
      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error adding subsection",
        description:
          error.message || "An error occurred while adding the subsection",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubsection = async () => {
    if (!editingSubsection) return;

    try {
      setIsSubmitting(true);

      // Auto-generate name from title if not provided
      const finalName =
        subsectionName.trim() ||
        subsectionTitle
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, "")
          .replace(/\s+/g, "_")
          .trim();

      // Update subsection using the API
      await documentOutputFormatApi.updateSubsection(editingSubsection.id, {
        title: subsectionTitle,
        name: finalName || undefined,
      });

      toast({
        title: "Subsection updated",
        description: "The subsection has been updated successfully",
      });

      setSubsectionTitle("");
      setSubsectionName("");
      setIsSubsectionNameManuallyEntered(false);
      setEditSubsectionDialogOpen(false);
      setEditingSubsection(null);
      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error updating subsection",
        description:
          error.message || "An error occurred while updating the subsection",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddField = async (fieldData: Partial<DocumentOutputField>) => {
    if (!selectedSubsectionId) {
      toast({
        variant: "destructive",
        title: "No subsection selected",
        description: "Please select a subsection to add the field to",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Get the selected subsection to determine field order
      const selectedSubsection = subsections.find(
        (s) => s.id === selectedSubsectionId
      );
      const fieldOrder = selectedSubsection?.fields?.length || 0;

      // Create field using the API
      await documentOutputFormatApi.createField(selectedSubsectionId, {
        title: fieldData.title!,
        name: fieldData.name,
        value: fieldData.value,
        value_source: fieldData.value_source || "predefined",
        field_order: fieldOrder,
        referenced_field_id: fieldData.referenced_field_id,
        comment: fieldData.comment,
      });

      toast({
        title: "Field added",
        description: "The field has been added successfully",
      });

      setAddFieldDialogOpen(false);
      setSelectedSubsectionId(null);
      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error adding field",
        description:
          error.message || "An error occurred while adding the field",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSubsection = async (subsectionId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this subsection and all its fields?"
      )
    ) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Delete subsection using the API
      await documentOutputFormatApi.deleteSubsection(subsectionId);

      toast({
        title: "Subsection deleted",
        description: "The subsection has been deleted successfully",
      });

      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error deleting subsection",
        description:
          error.message || "An error occurred while deleting the subsection",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteField = async (fieldId: number) => {
    if (!window.confirm("Are you sure you want to delete this field?")) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Delete field using the API
      await documentOutputFormatApi.deleteField(fieldId);

      toast({
        title: "Field deleted",
        description: "The field has been deleted successfully",
      });

      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error deleting field",
        description:
          error.message || "An error occurred while deleting the field",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateField = async (
    fieldId: number,
    updates: Partial<DocumentOutputField>
  ) => {
    try {
      setIsSubmitting(true);

      // Update field using the API
      await documentOutputFormatApi.updateField(fieldId, {
        title: updates.title,
        name: updates.name,
        value: updates.value,
        value_source: updates.value_source,
        field_order: updates.field_order,
        referenced_field_id: updates.referenced_field_id,
        comment: updates.comment,
      });

      toast({
        title: "Field updated",
        description: "The field has been updated successfully",
      });

      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error updating field",
        description:
          error.message || "An error occurred while updating the field",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border rounded-lg mb-6 bg-background">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center space-x-2">
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-medium">
              {section.title || `Section ${section.section_order + 1}`}
            </h3>
            <p className="text-sm text-muted-foreground">
              {subsections.length} subsection
              {subsections.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => {
              setSectionTitle(section.title);
              setEditDialogOpen(true);
            }}
            disabled={isSubmitting}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit Section
          </Button> */}

          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={() => onDeleteSection(section.id)}
            className="h-9 w-9 text-destructive hover:text-destructive/90"
            disabled={isSubmitting}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-muted-foreground">
            Subsections
          </h4>
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => setAddSubsectionDialogOpen(true)}
            disabled={isSubmitting}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Subsection
          </Button>
        </div>

        {subsections.length === 0 ? (
          <div className="p-4 text-center border-2 border-dashed rounded-md text-muted-foreground">
            No subsections found. Add subsections to organize your fields.
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable
              droppableId={`subsections-${section.id}`}
              type="subsections"
              direction="vertical"
            >
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4"
                >
                  {sortedSubsections.map((subsection, subsectionIndex) => (
                    <Draggable
                      key={`subsection-${subsection.id}`}
                      draggableId={`subsection-${subsection.id}`}
                      index={subsectionIndex}
                    >
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`${
                            snapshot.isDragging
                              ? "opacity-80 border-primary shadow-md"
                              : ""
                          }`}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing"
                                >
                                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                                </div>
                                <CardTitle className="text-base">
                                  {subsection.title}
                                </CardTitle>
                                <CardDescription className="text-sm text-muted-foreground">
                                  <div className="text-xs text-muted-foreground mt-1">
                                    <span className="font-medium">Name:</span>{" "}
                                    <code className="bg-muted px-1 py-0.5 rounded text-xs">
                                      {getSubsectionName(subsection)}
                                    </code>
                                  </div>
                                </CardDescription>
                                <span className="text-sm text-muted-foreground">
                                  ({subsection.fields.length} field
                                  {subsection.fields.length !== 1 ? "s" : ""})
                                </span>
                              </div>

                              <div className="flex items-center space-x-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  type="button"
                                  onClick={() => {
                                    setSelectedSubsectionId(subsection.id);
                                    setAddFieldDialogOpen(true);
                                  }}
                                  disabled={isSubmitting}
                                >
                                  <PlusCircle className="h-4 w-4 mr-1" />
                                  Add Field
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  type="button"
                                  onClick={() => {
                                    setEditingSubsection(subsection);
                                    setSubsectionTitle(subsection.title);
                                    setSubsectionName(subsection.name || "");
                                    setIsSubsectionNameManuallyEntered(
                                      !!subsection.name
                                    );
                                    setEditSubsectionDialogOpen(true);
                                  }}
                                  disabled={isSubmitting}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  type="button"
                                  onClick={() =>
                                    handleDeleteSubsection(subsection.id)
                                  }
                                  className="text-destructive hover:text-destructive/90"
                                  disabled={isSubmitting}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <Droppable
                              droppableId={`fields-${subsection.id}`}
                              type={`fields-${subsection.id}`}
                              direction="vertical"
                            >
                              {(provided) => (
                                <div
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                  className="space-y-2"
                                >
                                  {subsection.fields.length === 0 ? (
                                    <div className="p-3 text-center border-2 border-dashed rounded-md text-muted-foreground text-sm">
                                      No fields yet. Click "Add Field" to get
                                      started.
                                    </div>
                                  ) : (
                                    subsection.fields
                                      .sort(
                                        (a, b) => a.field_order - b.field_order
                                      )
                                      .map((field, fieldIndex) => (
                                        <Draggable
                                          key={`field-${field.id}`}
                                          draggableId={`field-${field.id}`}
                                          index={fieldIndex}
                                        >
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              className={`${
                                                snapshot.isDragging
                                                  ? "opacity-80 border-primary shadow-md"
                                                  : ""
                                              }`}
                                            >
                                              <DocumentFormField
                                                field={field}
                                                onDeleteField={
                                                  handleDeleteField
                                                }
                                                onUpdateField={
                                                  handleUpdateField
                                                }
                                                dragHandleProps={
                                                  provided.dragHandleProps
                                                }
                                                documentId={section.document_id}
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      ))
                                  )}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </CardContent>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Edit Section Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Section</DialogTitle>
            <DialogDescription>Update the section title.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="section-title" className="text-right">
                Title
              </label>
              <Input
                id="section-title"
                value={sectionTitle}
                onChange={(e) => setSectionTitle(e.target.value)}
                className="col-span-3"
                disabled={isSubmitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleEditSection}
              disabled={isSubmitting}
            >
              Update Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Subsection Dialog */}
      <Dialog
        open={addSubsectionDialogOpen}
        onOpenChange={setAddSubsectionDialogOpen}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Subsection</DialogTitle>
            <DialogDescription>
              Create a new subsection to organize fields within this section.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="subsection-title" className="text-right">
                Title <span className="text-destructive">*</span>
              </label>
              <Input
                id="subsection-title"
                placeholder="Subsection title"
                value={subsectionTitle}
                onChange={(e) => setSubsectionTitle(e.target.value)}
                className="col-span-3"
                disabled={isSubmitting}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="subsection-name" className="text-right">
                Name
              </label>
              <div className="col-span-3 space-y-1">
                <Input
                  id="subsection-name"
                  placeholder="subsection_name (optional)"
                  value={subsectionName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSubsectionName(value);
                    // If user clears the field, resume auto-generation
                    setIsSubsectionNameManuallyEntered(value.trim() !== "");
                  }}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Optional. Auto-generated from title if not provided.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAddSubsectionDialogOpen(false);
                setSubsectionTitle("");
                setSubsectionName("");
                setIsSubsectionNameManuallyEntered(false);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddSubsection}
              disabled={isSubmitting || !subsectionTitle.trim()}
            >
              Add Subsection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subsection Dialog */}
      <Dialog
        open={editSubsectionDialogOpen}
        onOpenChange={setEditSubsectionDialogOpen}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Subsection</DialogTitle>
            <DialogDescription>
              Update the subsection title and name.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="edit-subsection-title" className="text-right">
                Title <span className="text-destructive">*</span>
              </label>
              <Input
                id="edit-subsection-title"
                placeholder="Subsection title"
                value={subsectionTitle}
                onChange={(e) => setSubsectionTitle(e.target.value)}
                className="col-span-3"
                disabled={isSubmitting}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="edit-subsection-name" className="text-right">
                Name
              </label>
              <div className="col-span-3 space-y-1">
                <Input
                  id="edit-subsection-name"
                  placeholder="subsection_name (optional)"
                  value={subsectionName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSubsectionName(value);
                    // If user clears the field, resume auto-generation
                    setIsSubsectionNameManuallyEntered(value.trim() !== "");
                  }}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Optional. Auto-generated from title if not provided.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditSubsectionDialogOpen(false);
                setEditingSubsection(null);
                setSubsectionTitle("");
                setSubsectionName("");
                setIsSubsectionNameManuallyEntered(false);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleEditSubsection}
              disabled={isSubmitting || !subsectionTitle.trim()}
            >
              Update Subsection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Field Dialog */}
      <DocumentFormFieldDialog
        isOpen={addFieldDialogOpen}
        onClose={() => {
          setAddFieldDialogOpen(false);
          setSelectedSubsectionId(null);
        }}
        onSave={handleAddField}
        documentId={section.document_id}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default DocumentFormSection;
